import type { ReactNode } from 'react';
import { ScenarioSwitcher } from './ScenarioSwitcher';

interface Props {
  title: string;
  subtitle?: string;
  right?: ReactNode;
  /** Show the scenario switcher on the right edge. Default true for all
   *  pages that depend on the active scenario (dashboard, projection, events).
   *  Sub-pages that don't (Accounts, Assumptions, Update) can hide it. */
  showScenario?: boolean;
  onManageScenarios?: () => void;
}

/**
 * Page header. Scrolls with content (not sticky) so first-scroll gives the
 * chart/table maximum real estate. Scenario switcher on the right ensures
 * the active plan is always one click away from anywhere.
 */
export function TopBar({
  title,
  subtitle,
  right,
  showScenario = true,
  onManageScenarios,
}: Props) {
  return (
    <header
      className="bg-white dark:bg-ink-900"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2.5 lg:px-6">
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-lg font-bold text-ink-900 dark:text-ink-50 lg:text-xl">
            {title}
          </h1>
          {subtitle && (
            <p className="truncate text-xs text-ink-500 dark:text-ink-300">
              {subtitle}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <PrivacyPill />
          {right}
          {showScenario && onManageScenarios && (
            <ScenarioSwitcher onManage={onManageScenarios} />
          )}
        </div>
      </div>
    </header>
  );
}

function PrivacyPill() {
  return (
    <a
      href="#privacy"
      title="Cairn never sends your data over the network. Click for details."
      aria-label="Local-only privacy info"
      className="hidden items-center gap-1 rounded-full border border-ink-200 bg-ink-50 px-2 py-0.5 text-[10px] font-semibold text-ink-600 hover:border-accent-500 hover:text-accent-700 dark:border-ink-700 dark:bg-ink-800 dark:text-ink-300 dark:hover:border-accent-500 dark:hover:text-accent-500 sm:inline-flex"
    >
      <span aria-hidden>🔒</span>
      <span>Local-only</span>
    </a>
  );
}
